import type {Tile} from './types';

/**
 * Content for CVR-2667's Bento and Tabs modules. Finalized copy from Marketing (2026-09-25)
 * — Bento and Tabs now show genuinely different copy per destination (not one shared set),
 * so each surface gets its own dedicated tile content below rather than deriving both from
 * a single source. `gettext` is passed in from the calling component's `useI18n()` rather
 * than imported at module scope, since @udemy/i18n only exposes it as a hook.
 */

export type GetText = (text: string) => string;

/**
 * Bento (variant B): 1 large tile + 2 stacked tiles. Order matters — index 0 is the large
 * tile.
 */
export const getBentoTiles = (gettext: GetText): [Tile, Tile, Tile] => [
    {
        headline: gettext('Personal Plan: skills that move you forward'),
        description: gettext(
            'Upgrade to build, practice, and validate skills with one subscription.',
        ),
        // TODO(CVR-2667): hardcoded USD, embedded in a translatable string — every locale
        // sees literal "$13.00" and a translator could alter the number.
        footnote: gettext('Starting at $13.00/month'),
        ctaLabel: gettext('Learn more'),
        ctaUrl: '/personal-plan/',
        trackingId: 'lihp_marketing_messaging_pp_upsell',
    },
    {
        headline: gettext('Amplify your impact with AI'),
        description: gettext('Learn job-ready AI skills from our experts'),
        ctaLabel: gettext('Get started'),
        ctaUrl: '/topic/artificial-intelligence/',
        trackingId: 'lihp_marketing_messaging_nav_ai',
    },
    {
        headline: gettext('Get Certified, get ahead'),
        description: gettext('Prep, practice & purchase - all in one place'),
        ctaLabel: gettext('Explore certifications'),
        ctaUrl: '/browse/certification/',
        trackingId: 'lihp_marketing_messaging_nav_certifications',
    },
];

export interface TabFixture {
    id: string;
    title: string;
    tiles: Tile[];
}

/**
 * Tabs (variant C): one tab per tile, each with its own finalized copy
 */
export const getTabsFixtures = (gettext: GetText): TabFixture[] => [
    {
        id: 'lihp_marketing_messaging_pp_upsell',
        title: gettext('Subscribe'),
        tiles: [
            {
                eyebrow: gettext('Upgrade to Personal Plan'),
                headline: gettext('Build the skills to move your career forward'),
                description: gettext(
                    'Personal Plan gives you hands-on labs and AI role plays, certification prep, and skills assessments to track progress with one subscription.',
                ),
                ctaLabel: gettext('Upgrade today'),
                ctaUrl: '/personal-plan/',
                trackingId: 'lihp_marketing_messaging_pp_upsell',
            },
        ],
    },
    {
        id: 'lihp_marketing_messaging_nav_ai',
        title: gettext('Learn AI'),
        tiles: [
            {
                eyebrow: gettext('Amplify your impact with AI'),
                headline: gettext(
                    'Learn AI skills you can actually use on the job with courses taught by our experts.',
                ),
                ctaLabel: gettext('Get started'),
                ctaUrl: '/topic/artificial-intelligence/',
                trackingId: 'lihp_marketing_messaging_nav_ai',
            },
        ],
    },
    {
        id: 'lihp_marketing_messaging_nav_certifications',
        title: gettext('Get Certified'),
        tiles: [
            {
                eyebrow: gettext('Certification Preparation'),
                headline: gettext('Get certified, get ahead'),
                description: gettext(
                    'Prep for certifications with comprehensive courses, practice tests, and special offers on exam vouchers.',
                ),
                ctaLabel: gettext('Explore certifications'),
                ctaUrl: '/browse/certification/',
                trackingId: 'lihp_marketing_messaging_nav_certifications',
            },
        ],
    },
];
